// Provisioning batch status
export type BatchStatus =
  'pending' | 'processing' | 'processed' | 'failed' | 'cancelled';

// State for cascading county selector
export interface State {
  id: number;
  name: string;
  abbreviation: string;
}

// County from POST /queries/counties (via JBuilder); API returns snake_case, we use camelCase
export interface County {
  id: number;
  name: string;
  fips: string;
  stateAbbreviation: string;
}

// Provisioning batch
export interface ProvisioningBatch extends Record<string, unknown> {
  id: number;
  status: BatchStatus;
  mode: BatchMode;
  countyId: number | null;
  countyName: string | null;
  stateName: string | null;
  totalCount: number;
  enqueuedCount: number;
  completedCount: number;
  failedCount: number;
  skippedCount: number;
  entitiesCreatedCount: number;
  locationsCreatedCount: number;
  addressesCreatedCount: number;
  capacitiesCreatedCount: number;
  citationsCreatedCount: number;
  runByName: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  progressPercentage: number;
  // Detail-only fields
  parameters?: Record<string, unknown>;
  errorSummary?: Record<string, number>;
  sourceBreakdown?: Record<string, number>;
  pipelineBreakdown?: { direct: number; submission: number } | null;
}

// Provisioning batch submission (linked to its LLM provider batch)
export interface ProvisioningBatchSubmission extends Record<string, unknown> {
  id: number;
  providerBatchId: string | null;
  status: string;
  documentCount: number;
  completedCount: number;
  failedCount: number;
  progressPercentage: number;
  submittedAt: string | null;
  completedAt: string | null;
}

// Document returned by the documents endpoint
export interface ProvisioningDocument extends Record<string, unknown> {
  documentId: number;
  documentTitle: string | null;
  sourceDocumentId: number | null;
  extractionId: number;
  extractionStatus: string;
  completionId: number | null;
  completionStatus: string | null;
  entitiesCreated: number;
  locationsCreated: number;
  addressesCreated: number;
  capacitiesCreated: number;
  citationsCreated: number;
  errorMessage: string | null;
  providerBatchId: string | null;
  pipelinePath: 'direct' | 'submission' | null;
  startedAt: string;
}

// API Responses
export interface ProvisioningBatchesResponse {
  batches: ProvisioningBatch[];
  pagination: PaginationMeta;
}

export interface ProvisioningBatchResponse {
  batch: ProvisioningBatch;
}

export interface ProvisioningBatchSubmissionsResponse {
  submissions: ProvisioningBatchSubmission[];
  pagination: PaginationMeta;
}

export interface ProvisioningDocumentsResponse {
  documents: ProvisioningDocument[];
  pagination: PaginationMeta;
}

export interface PaginationMeta {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  perPage: number;
}

export interface CountyOption {
  id: number;
  name: string;
  stateName: string;
}

export interface ProvisioningStatsResponse {
  totalBatches: number;
  activeBatches: number;
  totalDocumentsProcessed: number;
  totalDocumentsFailed: number;
  totalEntitiesCreated: number;
  totalLocationsCreated: number;
  counties: CountyOption[];
}

// Pipeline execution modes
// provision: run base prompt for all docs (OCR if needed, then base prompt + post-prompts)
// post_prompts: skip base prompt for docs with stored LLM output, full pipeline for the rest
export type BatchMode = 'provision' | 'post_prompts';

export const batchModeLabels: Record<BatchMode, string> = {
  provision: 'Provision',
  post_prompts: 'Post Prompts',
};

export const batchModeTagColors: Record<BatchMode, CarbonTagType> = {
  provision: 'purple',
  post_prompts: 'teal',
};

export interface PromptVersion {
  id: number;
  category: string;
  version: string;
  name: string;
  default: boolean;
}

export type DocumentTypeFilter =
  'all' | 'courthouse' | 'non_courthouse' | 'regulatory';

export const documentTypeFilterLabels: Record<DocumentTypeFilter, string> = {
  all: 'All Document Types',
  courthouse: 'Courthouse',
  non_courthouse: 'Non-Courthouse',
  regulatory: 'Regulatory',
};

export type ProvisioningStatusFilter =
  'all' | 'provisioned' | 'not_provisioned';

export const provisioningStatusLabels: Record<
  ProvisioningStatusFilter,
  string
> = {
  all: 'All Documents',
  provisioned: 'Provisioned Only',
  not_provisioned: 'Not Provisioned Only',
};

export interface CreateBatchForm {
  stateId: number | null;
  countyId: number | null;
  mode: BatchMode;
  documentType: DocumentTypeFilter;
  provisioningStatus: ProvisioningStatusFilter;
  promptVersionIds: number[];
  provisionedDateStart: string | null;
  provisionedDateEnd: string | null;
}

export interface PreviewStats {
  total: number;
  unprovisioned: number;
  provisioned: number;
  withInterpretations: number;
  onLatestPrompt?: number;
}

export interface PromptVersionsResponse {
  promptVersions: PromptVersion[];
}

// Facet bucket from document query aggregations
export interface FacetBucket {
  key: string | number;
  count: number;
}

// Response shape from POST /queries/documents (subset relevant to preview)
export interface DocumentQueryResponse {
  totalCount: number;
  facets: {
    provisioningStatus: { buckets: FacetBucket[] } | null;
    hasInterpretationCount: number;
    promptIds: { buckets: FacetBucket[] } | null;
  };
}

// API response types (used by atoms.ts when wired to real API)
export interface CreateBatchResponse {
  batch: ProvisioningBatch;
}

export interface CreateBatchErrorResponse {
  errors: string[];
}

// Carbon Design System Tag `type` prop values
export type CarbonTagType =
  'gray' | 'blue' | 'green' | 'red' | 'teal' | 'purple';

// Tag color for extraction / completion statuses
export const extractionStatusColors: Record<string, CarbonTagType> = {
  pending: 'gray',
  processing: 'blue',
  complete: 'green',
  error: 'red',
};

// Tag color for submission statuses
export const submissionStatusColors: Record<string, CarbonTagType> = {
  submitted: 'blue',
  processing: 'blue',
  completed: 'green',
  partial_complete: 'teal',
  failed: 'red',
  expired: 'purple',
  cancelled: 'purple',
};

// Status tag color mapping
export const statusTagColors: Record<BatchStatus, CarbonTagType> = {
  pending: 'gray',
  processing: 'blue',
  processed: 'green',
  failed: 'red',
  cancelled: 'purple',
};

// Status display labels
export const statusLabels: Record<BatchStatus, string> = {
  pending: 'Pending',
  processing: 'Processing',
  processed: 'Processed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};
